/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

/*
 * Pure logic shared by scripts/split-i18next-namespaces.js and its guardrail
 * test (ui/shared/i18next/__tests__/index.test.ts). Lives under packages/
 * rather than scripts/ because the JS test Docker image's cache tarball
 * (Dockerfile.jenkins-cache) only copies a fixed set of directories in for
 * `yarn test:build`, and scripts/ isn't one of them -- a test importing
 * scripts/split-i18next-namespaces.js directly fails to resolve in CI even
 * though it works in a full local checkout.
 */

// Namespaces rendered globally/at boot -- ship inline to avoid a key-flash
// before their chunk loads.
const EAGER_NAMESPACES = ['common', 'navigation_header']

// Must match the featureBundles.ts entries wired to prefetch their
// namespace via Promise.all([import(...), i18next.loadNamespaces(ns)]).
// Only namespaces individually audited and safe to lazy-load -- see
// scripts/split-i18next-namespaces.js's file header for the audit
// criteria. quiz_statistics, discussion_topics_post, discussions_posts,
// discussions_v2, pages, and mentions were deliberately reverted back to
// eager after review found their shared ui/shared/ consumers use the
// legacy @canvas/i18n system, not i18next -- don't re-add them without
// solving that collision first.
//
// Still eager, audited but not yet safe to lazy-load:
// - no preload hook exists for React Router route.lazy() consumers
//   (ui/boot/initializers/router.tsx bypasses ui/loadBundle.ts entirely):
//   SmartSearch, account_grading_standards, account_grading_settings,
//   assignment_edit, rubrics-list-view, rubrics-import, sub_accounts
// - owned by too many unrelated bundles to list safely without a CI
//   guard against a future new importer in an unlisted bundle: planner,
//   shared_components, app_shared_components, differentiation_tags,
//   community, CalculationMethodContent, models_Assignment, calendar,
//   speed_grader, block_content_editor, feature_flags,
//   enhanced_individual_gradebook, conditional_release
const LAZY_ELIGIBLE_NAMESPACES = [
  'a2_AccessibleTipContent',
  'a2LatePolicyStatusDisplay',
  'a2LatePolicyToolTipContent',
  'accessibility_checker',
  'accessibility_course_statistics',
  'accessibility_scan',
  'account_calendar_settings',
  'account_calendar_settings_account_calendar_item',
  'account_calendar_settings_account_calendar_item_dropdown',
  'account_calendar_settings_account_group',
  'account_calendar_settings_account_list',
  'account_calendar_settings_account_tree',
  'account_calendar_settings_confirmation_modal',
  'account_calendar_settings_filter_controls',
  'account_calendar_settings_footer',
  'account_calendar_settings_utils',
  'account_calendars',
  'account_calendars_modal',
  'account_calendars_results_area',
  'account_course_user_search',
  'account_notification_settings',
  'account_settings',
  'account_settings_jsx_bundle',
  'AccountsTray',
  'add_edit_pseudonym',
  'AddGroup',
  'admin_split',
  'ai_experiences',
  'ai_experiences_ai_conversations',
  'ai_experiences_edit',
  'ai_experiences_show',
  'AlignmentSummary',
  'announcements_on_home_page',
  'announcements_v2',
  'appointment_groups',
  'appt_calendar_event_dialog',
  'assignment_2_assignment_toggle_details',
  'assignment_grade_summary',
  'assignment_index',
  'assignment_index_deep_linking_handlers',
  'assignment_index_menu',
  'assignment_show',
  'AssignmentGroupListItemView',
  'AssignmentListItemView',
  'assignments',
  'assignments.never_drop',
  'assignments.no_assignments_search',
  'assignments_2_attempt_tab',
  'assignments_2_elide_string',
  'assignments_2_external_tool',
  'assignments_2_file_upload',
  'assignments_2_initial_query',
  'assignments_2_login_action_prompt',
  'assignments_2_media_attempt',
  'assignments_2_MoreOptions',
  'assignments_2_MoreOptions_CanvasFiles',
  'assignments_2_peer_review',
  'assignments_2_shared_Steps',
  'assignments_2_student_annotation',
  'assignments_2_student_content',
  'assignments_2_student_content_rubric_self_assessment',
  'assignments_2_student_header',
  'assignments_2_student_header_date_title',
  'assignments_2_student_points_display',
  'assignments_2_submission_helpers',
  'assignments_2_submission_histories_query',
  'assignments_2_text_entry',
  'assignments_2_url_entry',
  'assignments_bulk_edit',
  'assignments_bulk_edit_use_save_assignment',
  'assignments_peer_reviews',
  'assignments_peer_reviews_student',
  'assignmentsIndexView',
  'assignmentsToggleShowByView',
  'auth_logging',
  'authentication_providers',
  'available_pronouns_list',
  'blueprint_course_child',
  'blueprint_course_sidebar',
  'blueprint_coursesExpandableLockOptions',
  'blueprint_settingsAssociationsTable',
  'blueprint_settingsCourseFilter',
  'blueprint_settingsCoursePicker',
  'blueprint_settingsCoursePickerTable',
  'blueprint_settingsMigrationOptions',
  'blueprint_settingsMigrationSync',
  'blueprint_settingsSyncChange',
  'blueprint_settingsSyncHistory',
  'blueprint_settingsUnsyncedChanges',
  'bounced_emails',
  'bulk_edit_students',
  'bulk_edit_students_actions',
  'bulk_edit_students_table',
  'calculator',
  'calculator_command',
  'calendar.header',
  'calendar_appointment_group_edit',
  'calendar_custom_recurring_event_natural_language',
  'calendar_custom_recurring_event_repeat_picker',
  'calendar_edit',
  'calendar_sidebar',
  'canvas_file_upload',
  'canvascareer',
  'change_password',
  'choose_mastery_path',
  'collaborations',
  'comm_messages',
  'CommentLibrary',
  'conferences',
  'conferences_alternatives',
  'confirmMasteryModal',
  'content_copy_redesign',
  'content_exports',
  'content_migrations',
  'content_migrations_redesign',
  'content_share_preview_overlay',
  'content_shares',
  'context_modules_v2',
  'context_prior_users',
  'context_roster_user_services',
  'conversations_2',
  'copy_course',
  'copy_warnings_modal',
  'course_color_selector',
  'course_default_due_time',
  'course_link_validator',
  'course_logging_details',
  'course_navigation_settings',
  'course_pace_date_input',
  'course_paces_actions',
  'course_paces_app',
  'course_paces_assignment_row',
  'course_paces_blackout_date_row',
  'course_paces_blackout_dates_modal',
  'course_paces_blackout_dates_table',
  'course_paces_blueprint_lock',
  'course_paces_change_tracking',
  'course_paces_empty_state',
  'course_paces_errors',
  'course_paces_flaggable_number_input',
  'course_paces_footer',
  'course_paces_header',
  'course_paces_modal',
  'course_paces_module',
  'course_paces_no_results',
  'course_paces_pace_picker',
  'course_paces_projected_dates',
  'course_paces_search',
  'course_paces_settings',
  'course_paces_time_selection',
  'course_restore',
  'course_search_results',
  'course_statistics',
  'course_stats',
  'course_template_details',
  'CourseAvailabilityOptions',
  'courses',
  'courses.show',
  'courses_show',
  'CoursesTray',
  'CreateEditAssignmentModal',
  'CreateEditAssignmentModalAdapter',
  'custom_emoji_deny_list',
  'custom_help_link',
  'dashboard',
  'dashboard_grades_page',
  'dashboard_grading_period_select',
  'default_account_quotas',
  'direct_share_course_import_panel',
  'discovery_page',
  'discussion_create',
  'discussion_insights',
  'discussion_pinned_post',
  'discussion_posts',
  'discussion_row',
  'discussion_settings',
  'discussion_topics',
  'discussion_topics_edit',
  'discussionTopicKeyboradShortcutModal',
  'DueDateOverrideView',
  'edit_section_dates',
  'edit_section_end_date',
  'edit_section_start_date',
  'edit_section_view',
  'editAssignmentGroupWeights',
  'editGroup',
  'EditMasteryScaleLink',
  'empty_course',
  'empty_modules',
  'enhanced_individual_gradebook_submit_score',
  'entity_search_form',
  'eportfolio',
  'epub_exports',
  'error_form',
  'external_content_cancel',
  'file_not_found',
  'files_v2',
  'filter_calendars_modal',
  'FindOutcomesModal',
  'folder_tree',
  'grade_change_logging_content',
  'grade_details',
  'grade_summary',
  'grade_summaryGroupView',
  'gradebook',
  'gradebook_default_gradebook_components_content_filters_module_filter',
  'gradebook_history',
  'gradebook_uploads',
  'gradebookActionMenu',
  'gradebookOutcomeGradebookView',
  'grades_empty_page',
  'grades_summary',
  'gradingGradeSummary',
  'gradinggradingPeriodCollection',
  'gradingPeriodTemplate',
  'group_submission_reminder',
  'GroupDetailView',
  'GroupsTray',
  'HelpTray',
  'homeroom_page',
  'horizon_toggle',
  'ignite_agent',
  'important_dates',
  'important_dates_empty',
  'ImportConfirmBox',
  'IndividualStudentMasteryOutcome',
  'internal-settings',
  'job_stats',
  'jobs',
  'jobs_v2',
  'k5_course',
  'k5_dashboard',
  'k5_dashboard_card',
  'keyboardShortcutModal',
  'knowledge_check_not_ready',
  'last_attended',
  'ldap_cert_upload',
  'learning_mastery_gradebook',
  'LearningMasteryGradebook',
  'license_help',
  'link_to_students',
  'link_validator',
  'login',
  'LogoutButton',
  'lti_registration.wizard',
  'lti_registrations',
  'manage_avatars',
  'MasteryScale',
  'media_immersive_view',
  'media_player_iframe_content',
  'MobileGlobalMenu',
  'MobileNavigation',
  'modelsQuiz',
  'moderate_quiz',
  'module_sequence_footer',
  'module_student_view_peer_reviews',
  'modules_home_page',
  'modules_page_icon_legend',
  'MoveOutcomesModal',
  'mutationActivity',
  'nav_tourpoints',
  'Navigation',
  'new_nav',
  'new_user_tutorial',
  'NewTabIndicator',
  'not_found_page_slide_puzzle',
  'notebook',
  'notebook_index',
  'outcome_alignment_v2',
  'outcome_detail',
  'outcome_management',
  'outcome_reporting',
  'OutcomeDescriptionModal',
  'OutcomeMoveModal',
  'outcomes_user_outcome_results',
  'overview_page',
  'paginated_list',
  'pairing_code_user_observees',
  'password_complexity_configuration',
  'peer-review-assignment-widget',
  'peer_review_allocation_rule_card',
  'peer_review_allocation_rules_tray',
  'peer_review_configuration_tray',
  'peer_review_student_select',
  'peer_reviews_student',
  'permission_button',
  'permissions',
  'permissions_role_tray',
  'permissions_role_tray_table_row',
  'permissions_v2',
  'permissions_v2_add_tray',
  'plugins',
  'ProfileTray',
  'progress_pill',
  'progressbar_view',
  'progression_module_header',
  'pseudonyms_mobile_login',
  'quantitative_data_options',
  'question_bank',
  'question_banks',
  'quiz_log_auditing',
  'quiz_migration_notifications',
  'quiz_show',
  'quiz_statistics',
  'quizzes.take_quiz',
  'quizzes_index',
  'quizzes_openModerateStudentDialog',
  'quizzes_public',
  'quizzes_quiz_submission',
  'quizzes_regrade_modal',
  'rate_limiting_settings',
  'react_collaborations',
  'react_developer_keys',
  'react_files',
  'react_scheduler',
  'registration',
  'remove_pace_warning_modal',
  'reset_pace_warning_modal',
  'restore_content_pane',
  'rootFoldersFinder',
  'roster_publicjs',
  'roster_section_input',
  'roster_section_selector',
  'rubrics-export',
  'section',
  'sections_tooltip',
  'security_panel',
  'self_enrollment',
  'self_unenrollment_modal',
  'sharedCurveGradesDialog',
  'sharedSetDefaultGradeDialog',
  'sidenav',
  'sis_import',
  'speed_grader_helpers',
  'SpeedGraderCheckpoints',
  'SpeedGraderDiscussionsNavigation',
  'SpeedGraderPostGradesMenu',
  'SpeedGraderSettingsMenu',
  'student_annotation_preview',
  'student_entries',
  'student_groups',
  'student_study_drawer',
  'submissions_show_preview',
  'submit_assignment',
  'submit_online_url_assignment',
  'suppress_notifications',
  'syllabus_revisions',
  'terms_index',
  'terms_of_service_modal',
  'terms_of_use',
  'theme_collection_view',
  'theme_editor',
  'todo',
  'todos_page',
  'top_navigation_tools',
  'unpublished_changes_button_props',
  'unpublished_changes_tray_contents',
  'unpublished_warning_modal',
  'upload_button',
  'use_switch_experience',
  'user',
  'user_date_range_search',
  'user_grades',
  'user_lists',
  'user_logins',
  'user_name',
  'user_profile',
  'user_restore',
  'user_search_results',
  'users',
  'video_conference',
  'webcam_modal',
  'webzip_export',
  'widget_dashboard',
  'wiki_page_index',
  'youtube_migration',
]

// Filename+webpack-request safe. Excludes the one legacy
// 'i18n!custom_emoji_deny_list' namespace (the '!' is a webpack loader
// delimiter, unsafe in a dynamic import request).
const SAFE_NS = /^[A-Za-z0-9._-]+$/

function splitNamespaces(en, lazyEligibleNamespaces) {
  const eager = {}
  const files = {}
  const skipped = []

  for (const [ns, keys] of Object.entries(en)) {
    const runtime = Object.fromEntries(Object.entries(keys).filter(([k, v]) => v !== k))

    if (EAGER_NAMESPACES.includes(ns)) {
      eager[ns] = runtime
    } else if (!SAFE_NS.test(ns)) {
      skipped.push(ns)
    } else if (Object.keys(runtime).length === 0) {
      // No value!==key content -- key-fallback is already correct English,
      // but still register an empty bundle. Otherwise i18next has no record
      // of this namespace at init, so any useTranslation(ns) call for it
      // goes through an async backend round-trip (and a bindI18nStore
      // re-render) on every load for nothing.
      eager[ns] = runtime
    } else if (lazyEligibleNamespaces.has(ns)) {
      files[ns] = runtime
    } else {
      eager[ns] = runtime
    }
  }

  const manifest = Object.keys(files).sort()

  return {eager, files, manifest, skipped}
}

module.exports = {splitNamespaces, EAGER_NAMESPACES, LAZY_ELIGIBLE_NAMESPACES}
